import { useState, useEffect, useCallback } from 'react';
import { X, FileText, Download, MessageSquare, LifeBuoy, Building2, ClipboardCheck, Send, AlertTriangle, Mail, CheckCircle2, XCircle, User } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/v1/admin/health-check`;
const fmt = (iso) => { try { return new Date(iso).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }); } catch { return ''; } };
const DOC_STATUSES = ['received', 'reviewed', 'needs re-upload', 'archived'];
const TICKET_STATUSES = ['Open', 'In progress', 'Resolved'];
const REQ_STATUSES = ['New', 'In review', 'Quoted', 'Closed'];
const TABS = [['profile', 'Client info', User], ['documents', 'Documents', FileText], ['messages', 'Messages', MessageSquare], ['tickets', 'Tickets', LifeBuoy], ['business', 'Business', Building2], ['requests', 'Requests', ClipboardCheck], ['email', 'Email log', Mail]];
const fmtAddr = (ad) => ad ? [ad.street, ad.city, ad.state, ad.zip].filter(Boolean).join(', ') : '';
const EMAIL_LABELS = {
  client_receipt: 'Payment receipt → client',
  admin_paid_alert: 'Paid alert → KGOB team',
  client_booking_confirmation: 'Booking confirmation → client',
  admin_booking_alert: 'Booking alert → KGOB team',
  client_reschedule_confirmation: 'Reschedule confirmation → client',
  client_login_code: 'Login code (OTP) → client',
  client_team_reply_notice: 'Team-reply nudge → client',
};

export const CaseDrawer = ({ engagementId, H, onClose }) => {
  const [a, setA] = useState(null);
  const [tab, setTab] = useState('documents');
  const [reply, setReply] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    const r = await fetch(`${API}/engagements?limit=300`, { headers: H });
    const d = await r.json();
    setA((d.engagements || []).find((e) => e.engagement_id === engagementId) || null);
  }, [engagementId, H]);
  useEffect(() => { load(); }, [load]);

  const call = async (path, opts = {}) => { setBusy(true); try { const r = await fetch(`${API}${path}`, { headers: { ...H, 'Content-Type': 'application/json' }, ...opts }); await load(); return r; } finally { setBusy(false); } };
  const download = async (d) => {
    const r = await fetch(`${API}/documents/${engagementId}/${d.id}/download`, { headers: H });
    if (!r.ok) return; const blob = await r.blob(); const url = URL.createObjectURL(blob);
    const link = document.createElement('a'); link.href = url; link.download = d.name; link.click(); URL.revokeObjectURL(url);
  };
  const setDocStatus = (did, status) => call(`/documents/${engagementId}/${did}`, { method: 'PATCH', body: JSON.stringify({ status }) });
  const sendMessage = async () => { const t = reply.trim(); if (!t) return; setReply(''); await call(`/engagements/${engagementId}/message`, { method: 'POST', body: JSON.stringify({ text: t }) }); };
  const ticketReply = async (tid, text, status) => { if (!text.trim()) return; await call(`/engagements/${engagementId}/tickets/${tid}/reply`, { method: 'POST', body: JSON.stringify({ text, status }) }); };
  const setReqStatus = (rid, status) => call(`/engagements/${engagementId}/requests/${rid}`, { method: 'PATCH', body: JSON.stringify({ status }) });

  return (
    <div className="fixed inset-0 z-50 flex justify-end" data-testid="hc-case-drawer">
      <div data-testid="case-drawer-div-1" className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative w-full max-w-2xl bg-white h-full shadow-2xl flex flex-col">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
          <div>
            <div className="font-semibold text-gray-900">{a?.contact?.name || 'Case file'}</div>
            <div className="text-xs text-gray-400">{a?.contact?.email} · {a?.contact?.business}</div>
            {a?.engagement_ref && <div className="text-xs font-mono text-[#C29B2E] mt-0.5" data-testid="hc-drawer-ref">Case {a.engagement_ref}</div>}
          </div>
          <div className="flex items-center gap-3">
            {a && (a.bridge_dirty
              ? <span className="text-xs inline-flex items-center gap-1 text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-2 py-0.5" data-testid="hc-drawer-sync"><AlertTriangle className="w-3 h-3" /> sync pending</span>
              : <span className="text-xs inline-flex items-center gap-1 text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2 py-0.5" data-testid="hc-drawer-sync">synced to app.kgob.com</span>)}
            <button onClick={onClose} data-testid="hc-drawer-close"><X className="w-5 h-5 text-gray-500" /></button>
          </div>
        </div>
        <div className="flex gap-1 px-4 border-b border-gray-200 overflow-x-auto">
          {TABS.map(([k, lbl, Icon]) => {
            const n = k === 'documents' ? a?.documents?.length : k === 'messages' ? a?.messages?.length : k === 'tickets' ? a?.tickets?.length : k === 'business' ? a?.businesses?.length : k === 'requests' ? a?.requests?.length : k === 'email' ? a?.email_log?.length : 0;
            return <button key={k} onClick={() => setTab(k)} className={`flex items-center gap-1.5 px-3 py-2.5 text-sm font-medium border-b-2 -mb-px whitespace-nowrap ${tab === k ? 'border-[#C29B2E] text-gray-900' : 'border-transparent text-gray-500'}`} data-testid={`hc-drawer-tab-${k}`}><Icon className="w-4 h-4" /> {lbl}{n ? ` (${n})` : ''}</button>;
          })}
        </div>
        <div className="flex-1 overflow-y-auto p-5">
          {!a ? <div className="text-sm text-gray-400 text-center py-10">Loading…</div> : (
            <>
              {tab === 'documents' && (a.documents?.length ? (a.documents || []).map((d) => (
                <div key={d.id} className="flex items-center gap-3 border border-gray-200 rounded-lg p-3 mb-2" data-testid={`hc-drawer-doc-${d.id}`}>
                  <FileText className="w-5 h-5 text-gray-400 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <button onClick={() => download(d)} className="text-sm font-medium text-[#0B2240] hover:underline truncate block" data-testid={`hc-drawer-doc-download-${d.id}`}>{d.name}</button>
                    <div className="text-xs text-gray-400">{d.year} · {d.type} · {fmt(d.uploaded_at)}</div>
                  </div>
                  <select value={d.status || 'received'} onChange={(e) => setDocStatus(d.id, e.target.value)} disabled={busy} className="text-xs border border-gray-300 rounded px-2 py-1" data-testid={`hc-drawer-doc-status-${d.id}`}>{DOC_STATUSES.map((s) => <option key={s}>{s}</option>)}</select>
                  <button data-testid="case-drawer-button-1" onClick={() => download(d)} className="text-gray-500" title="Download"><Download className="w-4 h-4" /></button>
                </div>
              )) : <Empty label="No documents uploaded." />)}

              {tab === 'messages' && (
                <div className="flex flex-col h-full">
                  <div className="flex-1 space-y-2 mb-3">
                    {a.messages?.length ? (a.messages || []).map((m, i) => (
                      <div key={m.id || i} className={`max-w-[80%] ${m.from === 'them' ? 'ml-auto text-right' : ''}`}>
                        <div className={`inline-block px-3 py-2 rounded-lg text-sm ${m.from === 'them' ? 'bg-[#0B2240] text-white' : 'bg-gray-100 text-gray-800'}`}>{m.text}</div>
                        <div className="text-[11px] text-gray-400 mt-0.5">{m.from === 'them' ? `You${m.by ? ` (${m.by})` : ''}` : 'Client'} · {fmt(m.ts)}</div>
                      </div>
                    )) : <Empty label="No messages yet." />}
                  </div>
                  <div className="flex gap-2 sticky bottom-0 bg-white pt-2 border-t border-gray-100">
                    <input value={reply} onChange={(e) => setReply(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && sendMessage()} placeholder="Reply to the client…" className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm" data-testid="hc-drawer-message-input" />
                    <button onClick={sendMessage} disabled={busy} className="bg-[#0B2240] text-white rounded-lg px-3 py-2 text-sm flex items-center gap-1 disabled:opacity-50" data-testid="hc-drawer-message-send"><Send className="w-4 h-4" /> Send</button>
                  </div>
                </div>
              )}

              {tab === 'tickets' && (a.tickets?.length ? (a.tickets || []).map((t) => <TicketBlock key={t.id} t={t} onReply={ticketReply} busy={busy} />) : <Empty label="No tickets." />)}

              {tab === 'profile' && (() => {
                const c = a.contact || {}; const p = a.profile || {};
                const Row = ({ label, value, testid }) => (
                  <div className="flex justify-between gap-4 py-2 border-b border-gray-100 text-sm">
                    <span className="text-gray-500">{label}</span>
                    <span className="text-gray-900 font-medium text-right" data-testid={testid}>{value || '—'}</span>
                  </div>
                );
                return (
                  <div className="border border-gray-200 rounded-lg p-4" data-testid="hc-drawer-profile">
                    {a.pii_status && (a.pii_status.complete
                      ? <div className="mb-3 flex items-center gap-2 text-sm text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2" data-testid="hc-drawer-pii-status"><CheckCircle2 className="w-4 h-4" /> All required PII collected</div>
                      : <div className="mb-3 flex items-start gap-2 text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2" data-testid="hc-drawer-pii-status"><AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" /><span><b>Missing before intake call:</b> {a.pii_status.missing.join(', ')}</span></div>)}
                    <Row label="Name" value={`${c.salutation || ''} ${c.first_name || ''} ${c.last_name || ''}`.trim()} />
                    <Row label="Email" value={c.email} />
                    <Row label="Phone" value={c.phone} />
                    <Row label="Role" value={c.role} />
                    <Row label="SSN / ITIN" value={p.ssn} testid="hc-drawer-ssn" />
                    <Row label="Date of birth" value={p.dob} />
                    <Row label="Home address" value={fmtAddr(p.address)} />
                  </div>
                );
              })()}

              {tab === 'business' && (a.businesses?.length ? (a.businesses || []).map((b) => (
                <div key={b.id} className="border border-gray-200 rounded-lg p-4 mb-2" data-testid={`hc-drawer-biz-${b.id}`}>
                  <div className="font-medium text-gray-900">{b.name}{b.dba ? ` · ${b.dba}` : ''}</div>
                  <div className="text-xs text-gray-500 mt-1">{[b.entity, b.state, b.ein && `EIN ${b.ein}`, b.formation_date && `Est. ${b.formation_date}`].filter(Boolean).join(' · ')}</div>
                  {fmtAddr(b.address) && <div className="text-xs text-gray-500 mt-1">{fmtAddr(b.address)}</div>}
                  {b.owners?.length > 0 && (
                    <div className="mt-2 space-y-1">
                      {(b.owners || []).map((o, i) => (
                        <div key={i} className="text-xs text-gray-600" data-testid={`hc-drawer-owner-${b.id}-${i}`}>
                          {o.first || ''} {o.last || ''}{o.role ? ` · ${o.role}` : ''}{o.pct ? ` · ${o.pct}%` : ''}{o.ssn ? ` · SSN/ITIN ${o.ssn}` : ''}{fmtAddr(o.address) ? ` · ${fmtAddr(o.address)}` : ''}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )) : <Empty label="No business details added." />)}

              {tab === 'requests' && (a.requests?.length ? (a.requests || []).map((r) => (
                <div key={r.id} className="border border-gray-200 rounded-lg p-4 mb-2" data-testid={`hc-drawer-req-${r.id}`}>
                  <div className="flex items-center justify-between gap-3">
                    <div className="font-medium text-gray-900">{r.title}</div>
                    <select value={r.status || 'New'} onChange={(e) => setReqStatus(r.id, e.target.value)} disabled={busy} className="text-xs border border-gray-300 rounded px-2 py-1" data-testid={`hc-drawer-req-status-${r.id}`}>{REQ_STATUSES.map((s) => <option key={s}>{s}</option>)}</select>
                  </div>
                  {r.areas?.length > 0 && <div className="text-xs text-gray-500 mt-1">{r.areas.join(', ')}</div>}
                  <p className="text-sm text-gray-600 mt-2">{r.detail}</p>{r.timeline && <div className="text-xs text-gray-400 mt-1">Timeline: {r.timeline}</div>}
                </div>
              )) : <Empty label="No custom requests." />)}

              {tab === 'email' && (() => {
                const log = [...(a.email_log || [])].reverse();
                return log.length ? (log || []).map((e) => (
                  <div key={e.id} className="flex items-center gap-3 border border-gray-200 rounded-lg p-3 mb-2" data-testid={`hc-drawer-email-${e.id}`}>
                    {e.ok ? <CheckCircle2 className="w-5 h-5 text-emerald-500 shrink-0" /> : <XCircle className="w-5 h-5 text-red-500 shrink-0" />}
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-gray-900">{EMAIL_LABELS[e.type] || e.type}</div>
                      <div className="text-xs text-gray-400">{e.to ? `${e.to} · ` : ''}{fmt(e.sent_at)}{e.ok ? '' : ` · FAILED: ${e.error || 'send error'}`}</div>
                    </div>
                    <span className={`text-[11px] px-2 py-0.5 rounded-full ${e.ok ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-700'}`}>{e.ok ? 'sent' : 'not sent'}</span>
                  </div>
                )) : <Empty label="No emails sent yet for this client." />;
              })()}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

const Empty = ({ label }) => <div className="text-sm text-gray-400 text-center py-10">{label}</div>;

const TicketBlock = ({ t, onReply, busy }) => {
  const [text, setText] = useState('');
  const [status, setStatus] = useState(t.status);
  return (
    <div className="border border-gray-200 rounded-lg p-4 mb-3" data-testid={`hc-drawer-ticket-${t.id}`}>
      <div className="flex items-center justify-between gap-2">
        <div className="font-medium text-gray-900">{t.subject}</div>
        <span className="text-xs font-mono text-gray-400">{t.ref}</span>
      </div>
      <div className="text-xs text-gray-500 mt-1">{t.category} · {t.priority} · <span className="font-medium">{t.status}</span></div>
      <div className="mt-3 space-y-2">
        {(t.thread || []).map((m, i) => (
          <div key={i} className={`text-sm ${m.from === 'them' ? 'text-right' : ''}`}>
            <span className={`inline-block px-3 py-1.5 rounded-lg ${m.from === 'them' ? 'bg-[#0B2240] text-white' : 'bg-gray-100'}`}>{m.text}</span>
            <div className="text-[11px] text-gray-400">{m.from === 'them' ? 'You' : 'Client'} · {fmt(m.ts)}</div>
          </div>
        ))}
      </div>
      <div className="flex gap-2 mt-3">
        <input value={text} onChange={(e) => setText(e.target.value)} placeholder="Reply…" className="flex-1 border border-gray-300 rounded px-2 py-1.5 text-sm" data-testid={`hc-drawer-ticket-reply-${t.id}`} />
        <select data-testid="case-drawer-select-1" value={status} onChange={(e) => setStatus(e.target.value)} className="text-xs border border-gray-300 rounded px-2">{TICKET_STATUSES.map((s) => <option key={s}>{s}</option>)}</select>
        <button onClick={() => { onReply(t.id, text, status); setText(''); }} disabled={busy} className="bg-[#0B2240] text-white rounded px-3 text-sm disabled:opacity-50" data-testid={`hc-drawer-ticket-send-${t.id}`}>Send</button>
      </div>
    </div>
  );
};
